import * as React from "react"
import { Component, memo, useState } from "react"
import { Appearance, StyleProp, TextStyle, ViewStyle } from "react-native"
import { Text } from "../text/text"
import { FormControl as BaseFormControl, Icon, IFormControlProps } from "native-base"
import { color, nativeBaseTheme } from "../../theme"
import { TxKeyPath } from "../../i18n"
import { TextPresets } from "../text/text.presets"
import { Input, InputProps } from "../input/input"
import MaterialIcons from "react-native-vector-icons/MaterialIcons"
import _ from "lodash"
import { ColorType } from "native-base/lib/typescript/components/types"
import { TextProps } from "../text/text.props"

const LABEL_STYLE: ViewStyle = {
  paddingVertical: 2,
  width: "100%"
}

export interface FormControlProps extends IFormControlProps {
  labelTx?: TxKeyPath,
  label?: string,
  labelPreset?: TextPresets,
  labelColor?: ColorType,
  labelStyle?: StyleProp<TextStyle>,
  labelComponent?: Component | React.ReactElement,

  helperTextTx?: TxKeyPath,
  helperText?: string,
  helperTextPreset?: TextPresets,
  helperTextStyle?: StyleProp<TextStyle>,
  helperTextProps?: TextProps,
  helperTextComponent?: Component | React.ReactElement,

  errorMessageTx?: TxKeyPath,
  errorMessage?: string,
  errorMessagePreset?: TextPresets,
  errorMessageStyle?: StyleProp<TextStyle>,
  errorMessageProps?: TextProps,
  errorMessageComponent?: Component,
  errorLines?: number

  inputProps?: InputProps,

  colorScheme?: "light" | "dark"
}

const defaultInputProps: InputProps = {}

/**
 * Describe your component here
 */
export const FormControl = memo(function FormControl(props: FormControlProps) {
  const {
    labelTx, label, labelPreset, labelColor, labelStyle, labelComponent,
    inputProps = defaultInputProps, isDisabled,
    helperTextTx, helperText, helperTextPreset, helperTextStyle, helperTextProps, helperTextComponent,
    errorMessageTx, errorMessage, errorMessagePreset, errorMessageStyle, errorMessageProps, errorMessageComponent,
    colorScheme = Appearance.getColorScheme(), errorLines,
    style,
    ...restProps
  } = props

  const [ showPassword, setShowPassword ] = useState<boolean>(false)
  const styles = [style, { paddingVertical: 8 }]

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });
  const formControlLabelStyle = nativeBaseTheme.components.FormControlLabel.baseStyle({ colorMode: colorScheme });
  const formControlHelperStyle = nativeBaseTheme.components.FormControlHelperText.baseStyle({ colorMode: colorScheme });
  const formControlErrorMessageStyle = nativeBaseTheme.components.FormControlErrorMessage.baseStyle({ colorMode: colorScheme });

  return (
    <BaseFormControl {...restProps} isDisabled={isDisabled} style={styles}>
      {(labelComponent || label || labelTx) && (
        <BaseFormControl.Label {...formControlLabelStyle} style={LABEL_STYLE}>
          {labelComponent || <Text tx={labelTx} text={label} preset={labelPreset || "title4"} colorScheme={colorScheme} color={labelColor} style={labelStyle} numberOfLines={1} />}
        </BaseFormControl.Label>
      )}
      {props.children && (typeof props.children === 'function' && props.children(inputProps) || props.children) || (
        <Input
          colorScheme={colorScheme}
          InputLeftElement={(
            inputProps.InputLeftElement ||
            (inputProps.type === "password" && <Icon as={<MaterialIcons name="lock"></MaterialIcons>} size={"24px"} ml={"9px"} textAlign="center" color={color.palette.gray500} />) ||
            <></>
          )}
          InputRightElement={(
            inputProps.InputRightElement ||
            (inputProps.type === "password" && <Icon {...iconBaseStyle} as={<MaterialIcons name={showPassword ? "visibility" : "visibility-off"}></MaterialIcons>} testID={`${(inputProps.testID || '')}-show-password`} size={"24px"} mr={"9px"} textAlign="center" onPress={() => setShowPassword(!showPassword)} />) ||
            <></>
          )}
          {...inputProps}
          type={inputProps.type === "password" && showPassword ? "text" : inputProps.type}
          isDisabled={isDisabled}
        />
      )}
      {(helperTextComponent || helperText || helperTextTx) && (
        <BaseFormControl.HelperText {...formControlHelperStyle}>
          {helperTextComponent || <Text tx={helperTextTx} text={helperText} preset={helperTextPreset || "caption2"} colorScheme={colorScheme} {...formControlHelperStyle._text} style={helperTextStyle} {...helperTextProps} /> }
        </BaseFormControl.HelperText>
      )}
      {(errorMessageComponent || errorMessage || errorMessageTx) && (
        <BaseFormControl.ErrorMessage {...formControlErrorMessageStyle} _stack={{ flexDirection: "column", alignItems: "flex-start" }}>
          {errorMessageComponent || <Text tx={errorMessageTx} text={_.upperFirst(errorMessage)} preset={errorMessagePreset || "caption2"} colorScheme={colorScheme} numberOfLines={errorLines} {...formControlErrorMessageStyle._text} {...errorMessageProps} style={errorMessageStyle} /> }
        </BaseFormControl.ErrorMessage>
      )}
    </BaseFormControl>
  )
})
